// material
import {
  Container,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton
} from '@mui/material';
import { Icon } from '@iconify/react';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../routes/paths';
import { checkRoles, ROLE_USER } from 'guards/CheckRoles';
import { generatePath, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SvgIconStyle from '../../components/SvgIconStyle';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as React from 'react';
import { useState } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useSelector, RootState } from '../../redux/store';
import { ProductSchedule } from '../../@types/model/productSchedule';
import { getProductSchedule, productScheduleState } from 'recoil/productScheduleState';
import ConfirmDialog from 'components/ConfirmDialog';

import axios from '../../utils/axios';
import { MIconButton } from 'components/@material-extend';
import closeFill from '@iconify/icons-eva/close-fill';
import { useSnackbar } from 'notistack';

export default function ProductScheduleList() {
  const { themeStretch } = useSettings();
  const { t } = useTranslation();
  const [loading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [productScheduleDeletionGuid, setProductScheduleDeletionGuid] = useState('');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const productSchedules: ProductSchedule[] = useRecoilValue(productScheduleState);
  const setProductSchedule = useSetRecoilState(productScheduleState);

  React.useEffect(() => {
    getProductSchedule().then((productSchedules: ProductSchedule[]) => {
      setProductSchedule(productSchedules);
    });
  }, []);

  function deleteProductSchedule() {
    axios.delete('/api/v2/product-schedules' + productScheduleDeletionGuid).then(() => {
      enqueueSnackbar(t('delete_product_schedule'), {
        variant: 'success',
        action: (key) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <Icon icon={closeFill} />
          </MIconButton>
        )
      });
      getProductSchedule().then((productSchedules: ProductSchedule[]) => {
        setProductSchedule(productSchedules);
      });
    });
  }

  const { user } = useSelector((state: RootState) => state.user);
  const role = checkRoles(user, ROLE_USER) ? '/' : '/end-user';
  const pageTitle = t('product_schedule');
  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading={pageTitle}
          links={[
            { name: t('home'), href: role },
            { name: pageTitle, href: PATH_DASHBOARD.deliveryConfigurations.list }
          ]}
          action={
            <Button
              variant={'contained'}
              component={NavLink}
              to={PATH_DASHBOARD.productSchedule.new}
            >
              {t('add_product_schedule')}
            </Button>
          }
        />
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('reference')}</TableCell>
                <TableCell align="center">{t('template')}</TableCell>
                <TableCell align="center">{t('type')}</TableCell>
                <TableCell align="center">{t('n_devices')}</TableCell>
                <TableCell align="center">{t('n_items')}</TableCell>
                <TableCell align="center">{t('n_kilos_consumed')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell align="right">{t('options')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productSchedules.map((ps: ProductSchedule) => {
                return (
                  <TableRow
                    key={ps.guid}
                    style={ps.state !== 'active' ? { background: '#ffd5d5' } : {}}
                  >
                    <TableCell>{ps.reference}</TableCell>
                    <TableCell align="center">{ps.product_schedule_template?.name}</TableCell>
                    <TableCell align="center">
                      {t(ps.product_schedule_template?.type?.replace('-', '_') ?? '')}
                    </TableCell>
                    <TableCell align="center">{ps.devices?.length ?? ''}</TableCell>
                    <TableCell align="center">{ps.schedule?.length ?? ''}</TableCell>
                    <TableCell align="center">
                      {ps.schedule
                        ?.map((i) => i.consumed)
                        .reduce((partialSum, a) => partialSum + (a as number), 0) ?? ''}{' '}
                      /{' '}
                      {ps.schedule
                        ?.map((i) => i.quantity)
                        .reduce((partialSum, a) => partialSum + (a as number), 0) ?? ''}
                    </TableCell>
                    <TableCell align="center">{t(ps.state)}</TableCell>
                    <TableCell align="right">
                      {ps.state === 'active' && (
                        <div>
                          <IconButton
                            component={NavLink}
                            to={generatePath(PATH_DASHBOARD.productSchedule.edit, {
                              guid: ps.guid
                            })}
                          >
                            <SvgIconStyle
                              src="/static/icons/giraf/edit.svg"
                              sx={{ width: '20px', height: '20px' }}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setProductScheduleDeletionGuid(ps.guid);
                              setConfirmOpen(true);
                            }}
                          >
                            <SvgIconStyle
                              src="/static/icons/giraf/remove.svg"
                              sx={{ width: '20px', height: '20px' }}
                            />
                          </IconButton>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Container>

      <ConfirmDialog
        title={t('delete_product_schedule')}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={() => {
          deleteProductSchedule();
        }}
      >
        {t('confirm_delete_product_schedule')}
      </ConfirmDialog>
    </Page>
  );
}
